// Eskom PARCC — Transactions detail panel (read-only).
//
// R3: the meta grid is tightened, assignee/reviewer render as resolved names
// (PersonChip), compliance/CES show as a status icon + full text (legible in both
// themes, unlike the old space-in-classname badge), and the working-paper notes are
// rendered FULL-WIDTH and structured (WorkingPaperNotes) instead of squashed into a
// 160px grid cell. Still read-only — editing lives in TransactionEditor.
import { Card, Button, StatusIcon, PersonChip, WorkingPaperNotes } from '../../components/ui';
import { formatRand } from '../../lib/money';
import { formatDate } from '../../lib/formats';
import { hasRealLink } from '../../domain/links';
import { useNameDirectory } from '../../hooks';
import { TransactionHistory } from '../audit/TransactionHistory';
import type { NameDirectory } from '../../lib/people';
import type { Transaction } from '../../data/types';
import type { FindingRead } from '../../lib/db';
import { isReviewed } from './editTransaction';

const EMPTY_DIRECTORY: NameDirectory = new Map();

/** A labelled value tile in the `.meta` grid; skips blanks so the grid stays tidy. */
function MetaItem({ label, value }: { label: string; value: React.ReactNode }): React.JSX.Element | null {
  if (value === undefined || value === null || value === '') return null;
  return (
    <div className="item">
      <div className="k">{label}</div>
      <div>{value}</div>
    </div>
  );
}

/** A compliance/CES level shown as a tone icon + its full text. */
function StatusText({ level }: { level: string | null | undefined }): React.JSX.Element {
  const label = typeof level === 'string' && level.trim() !== '' ? level : '—';
  return (
    <span className="status-cluster">
      <StatusIcon value={level} size={15} />
      {label}
    </span>
  );
}

export function TransactionDetail({
  tx,
  compliance,
  findings,
  canEdit = false,
  onEdit,
  onCaptureCes,
}: {
  tx: Transaction;
  /** Pre-computed effective compliance level for this transaction. */
  compliance: string;
  /** This transaction's finding lines (already filtered by referenceNo). */
  findings: readonly FindingRead[];
  /** Whether the current user may edit this transaction (drives the Edit + CES buttons). */
  canEdit?: boolean;
  /** Enter edit mode. Only invoked when `canEdit` is true. */
  onEdit?: () => void;
  /** Open the Compliance Evaluation Sheet capture modal. Only when `canEdit` is true. */
  onCaptureCes?: () => void;
}): React.JSX.Element {
  const directory = useNameDirectory().data ?? EMPTY_DIRECTORY;
  const subtitle = [tx.dataset, tx.recordLabel, tx.division, tx.period]
    .filter((v): v is string => typeof v === 'string' && v.length > 0)
    .join(' · ');

  return (
    <Card>
      <div className="summarybox">
        <div>
          <div style={{ fontSize: 'var(--fs-2xl)', fontWeight: 800 }}>{tx.referenceNo}</div>
          {subtitle && <div className="small">{subtitle}</div>}
          <div style={{ marginTop: 6 }}>
            <StatusText level={compliance} />
          </div>
        </div>
        <div className="actions">
          {hasRealLink(tx) && (
            // R3 #3: labelled explicitly — this is the OpenText document folder.
            // (OpenText requires Microsoft Edge on the Eskom network.) Gated on a
            // REAL link so a placeholder cell ("TBC"/"N/A") never renders a dead anchor.
            <a className="btn" href={tx.link} target="_blank" rel="noopener noreferrer">
              OpenText
            </a>
          )}
          {canEdit && onCaptureCes && (
            <Button onClick={onCaptureCes} title="Compliance Evaluation Sheet">
              CES
            </Button>
          )}
          {canEdit && onEdit && (
            <Button variant="primary" onClick={onEdit}>
              Edit
            </Button>
          )}
        </div>
      </div>

      <div className="meta">
        <MetaItem label="Reference No" value={tx.referenceNo} />
        <MetaItem label="Dataset" value={tx.dataset} />
        <MetaItem label="Record label" value={tx.recordLabel} />
        <MetaItem label="Division" value={tx.division} />
        <MetaItem label="Period" value={tx.period} />
        <MetaItem label="Year" value={tx.year} />
        <MetaItem label="Supplier" value={tx.supplier} />
        <MetaItem label="CSD number" value={tx.csdNumber} />
        <MetaItem label="Value" value={tx.value ? formatRand(tx.value) : tx.valueDisplay} />
        <MetaItem label="Mechanism" value={tx.mechanism} />
        {/* R3 #3: the working paper's grey header fields, surfaced for reference. */}
        <MetaItem label="BU" value={tx.bu ?? tx.ou} />
        <MetaItem label="Brief description" value={tx.briefDescription} />
        <MetaItem label="Contract/PO number" value={tx.contractNumber} />
        <MetaItem label="Buyer" value={tx.buyer} />
        <MetaItem label="PR number" value={tx.prNumber} />
        <MetaItem label="Procurement manager" value={tx.procurementManager} />
        <MetaItem label="End-user" value={tx.endUser} />
        <MetaItem label="Reviewed" value={<StatusIcon reviewed={isReviewed(tx)} size={15} />} />
        {/* Owner feedback #8: date stamps for when the transaction was reviewed /
            captured (MetaItem hides a blank, so an unstamped row shows nothing). */}
        <MetaItem label="Reviewed on" value={formatDate(tx.reviewedAt) || null} />
        <MetaItem label="Captured on" value={formatDate(tx.capturedDate) || null} />
        <MetaItem label="Gaps status" value={tx.gapsStatus} />
        <MetaItem label="Compliance level" value={tx.complianceLevel ? <StatusText level={tx.complianceLevel} /> : null} />
        <MetaItem
          label="Compliance override"
          value={tx.complianceLevelOverride ? <StatusText level={tx.complianceLevelOverride} /> : null}
        />
        <MetaItem label="Self-declaration" value={tx.selfDeclaration} />
        <MetaItem
          label="Assigned to"
          value={tx.assignedToEmail ? <PersonChip email={tx.assignedToEmail} directory={directory} /> : null}
        />
        <MetaItem
          label="Reviewer"
          value={tx.reviewerEmail ? <PersonChip email={tx.reviewerEmail} directory={directory} /> : null}
        />
      </div>

      {tx.workingPaperNotes && (
        <div style={{ marginBottom: 16 }}>
          <div className="k" style={{ marginBottom: 8 }}>
            Working-paper notes
          </div>
          <WorkingPaperNotes text={tx.workingPaperNotes} />
        </div>
      )}

      <div>
        <div style={{ fontWeight: 700, margin: '4px 0 10px' }}>
          Findings{findings.length ? ` (${findings.length})` : ''}
        </div>
        {findings.length === 0 ? (
          <div className="empty">No finding lines recorded for this transaction.</div>
        ) : (
          findings.map((f) => <FindingCard key={f.id} finding={f} />)
        )}
      </div>

      {/* Per-transaction audit trail — who changed what, where and when. Visible to
          everyone who can see the transaction; lazy (fetches only when expanded). */}
      <TransactionHistory referenceNo={tx.referenceNo} />

      {!canEdit && (
        <p className="small" style={{ marginTop: 12 }}>
          Read-only view. You may edit only transactions allocated to you.
        </p>
      )}
    </Card>
  );
}

/** A single read-only finding line, mirroring the monolith's `.finding-card`. */
function FindingCard({ finding: f }: { finding: FindingRead }): React.JSX.Element {
  const level = f.finalCompliance || f.suggestedCompliance;
  const meta = [
    f.source,
    f.legacyStatus && `Legacy status: ${f.legacyStatus}`,
    f.effectiveStatus && `Effective status: ${f.effectiveStatus}`,
  ].filter((v): v is string => typeof v === 'string' && v.length > 0);
  const anchors = f.anchors ?? [];
  const rules = (f.matchedRules ?? []).slice(0, 3);

  return (
    <div className="finding-card">
      <div className="finding-head">
        <div className="finding-text">{f.text || 'Finding'}</div>
        <StatusText level={level} />
      </div>
      {meta.length > 0 && <div className="small">{meta.join(' · ')}</div>}
      <div className="small" style={{ margin: '6px 0 2px' }}>
        Route: {(f.route || 'generic').toUpperCase()}
        {f.confidence ? ` · Confidence: ${f.confidence}` : ''}
      </div>
      {(rules.length > 0 || anchors.length > 0) && (
        <div className="anchor-list">
          {rules.map((r) => (
            <span className="anchor" key={`r-${r}`}>
              {r}
            </span>
          ))}
          {anchors.map((a) => (
            <span className="anchor" key={`a-${a}`}>
              {a}
            </span>
          ))}
        </div>
      )}
      {f.rationale && (
        <div className="small" style={{ marginTop: 8 }}>
          <strong>Rationale:</strong> {f.rationale}
        </div>
      )}
      {f.reviewNotes && (
        <div className="small" style={{ marginTop: 6 }}>
          <strong>Review notes:</strong> {f.reviewNotes}
        </div>
      )}
      {f.seeded && (
        <div style={{ marginTop: 8 }}>
          <span className="anchor">Seeded</span>
        </div>
      )}
    </div>
  );
}
